import React from 'react';

export default function EvidenceDisplay({ evidence, onReview, canReview }) {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getEvidenceTypeIcon = (type) => {
    const icons = {
      email: (
        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
        </svg>
      ),
      meeting_notes: (
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      ),
      document: (
        <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      ),
      other: (
        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[type] || icons.other;
  };

  if (!evidence || evidence.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">No Evidence Uploaded</h4>
        <p className="text-gray-600">Upload evidence of senior executive approval to proceed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {evidence.map((item) => (
        <div
          key={item.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-3 flex-1">
              {getEvidenceTypeIcon(item.evidence_type)}
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  {item.executive_title} Approval
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center gap-4">
                    <span><strong>Approval Date:</strong> {new Date(item.approval_date).toLocaleDateString()}</span>
                    <span><strong>Uploaded:</strong> {new Date(item.uploaded_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span><strong>Type:</strong> {item.evidence_type.replace('_', ' ')}</span>
                    <span><strong>By:</strong> {item.uploaded_by_name}</span>
                  </div>
                  {item.reviewed_by_name && (
                    <div className="flex items-center gap-4">
                      <span><strong>Reviewed:</strong> {new Date(item.reviewed_at).toLocaleDateString()}</span>
                      <span><strong>By:</strong> {item.reviewed_by_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(item.status)}
              {canReview && item.status === 'pending' && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onReview(item.id, 'approved')}
                    className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onReview(item.id, 'rejected')}
                    className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {item.description && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
              <div className="text-sm text-gray-700">
                <strong>Description:</strong> {item.description}
              </div>
            </div>
          )}

          {item.file_name && (
            <div className="mt-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-600">{item.file_name}</span>
              <span className="text-xs text-gray-500">({item.file_size ? `${Math.round(item.file_size / 1024)}KB` : 'Unknown size'})</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
